/**
 * The one object main.js holds to be multiplayer.
 *
 * Everything network-shaped funnels through here so the game's own wiring
 * stays a handful of lines: construct, connect, join, and then two calls a
 * frame.  The seams underneath -- socket (client.js), render-past clock
 * (client.js + interp.js), other people's cats (roster.js) -- never appear
 * in main.js at all.
 *
 * CONSTRUCTION ORDER MATTERS: build Net after the base cats load and BEFORE
 * the Customizer is constructed.  The roster clones its pristine rig
 * templates from the bases at this moment, and the customizer writes the
 * local player's look straight into those bases' bones and materials --
 * roster.js's header carries the full argument.
 *
 * Offline is not an error state here, it is a supported platform: on file://
 * (the artifact build) or where nobody answers /ws, `connect` stands the
 * whole layer down and every method below quietly no-ops.  The game must
 * play exactly as the single-player build did, which is why nothing in this
 * file throws, alerts, or logs on the no-server path.
 */
import { Client, OPENED, CLOSED } from "./client.js";
import { Roster } from "./roster.js";
import { desktopBridge } from "./desktop.js";
import { localToken, saveToken } from "../identity.js";
import { DENY, CLOSE } from "@catworld/shared";
import { PROTO_VERSION } from "@catworld/shared";

/**
 * The deny sentence shown INSTEAD of DENY.VERSION's when the desktop bridge
 * is present -- "update Cat World on Steam", not "refresh the page".  A
 * client-local display key, never a wire value: join.js's SENTENCES holds
 * the sentence under this same literal (the two spellings must match; a
 * future G-4 contract could pin them).
 */
const VERSION_STEAM = "version-steam";

/**
 * Outbound look/name pacing.  The customizer's onLook fires per input event
 * -- every keystroke of a rename, every pixel of a slider drag, easily 60+
 * a second -- and the server's flood gate (MSG_RATE 60/s sustained) would
 * close the socket of anyone enthusiastic with a slider.  Trailing-edge
 * throttle: intermediate values coalesce, the LAST value always goes out, so
 * the street sees a cat settle into its final shape at most LOOK_MS behind
 * the hand that shaped it.
 */
const LOOK_MS = 150;
const NAME_MS = 400;   // slower: a half-typed name is noise, a half-dragged
                       // slider at least looks like something

export class Net {
  /**
   * @param bases      the loaded Cat instances, index-aligned with CATS/BASES
   * @param scene      the scene remote cats join
   * @param camera     unused today; accepted so a distance-based degrade can
   *                   land in roster.js without touching main.js again
   * @param callbacks  { onClaim({slot,id,token}), onRoster(cats),
   *                     onNeeds(wants, progress), onBliss(slot, placeId),
   *                     onStatus({connected, rtt, denied}) } -- all optional
   */
  constructor({ bases, scene, camera, callbacks = {} } = {}) {
    this.callbacks = callbacks;
    this.camera = camera;
    this.client = new Client();
    this.roster = new Roster({ bases, scene });

    this._slot = -1;
    this._id = null;
    this._denied = null;
    this._deniedOpen = false;
    this._joinArgs = null;       // remembered so every (re)connect can re-join
    this._retriedStale = false;  // one fresh-cat retry per session, not a loop
    this._lookTimer = 0;
    this._lookPending = null;
    this._lookSentAt = 0;
    this._nameTimer = 0;
    this._namePending = null;
    this._humanRev = 0;
    /** Somebody else's cat spoke: (slot, phraseIndex) => void.  An
        ASSIGNABLE field rather than a constructor callback -- the same
        contract customizer.onLook already uses -- because the quick-chat
        object is built at module load, before Net exists, and wires itself
        up lazily (chat.js's update).  Adding it to `callbacks` instead
        would grow main.js's Net construction, which the wiring rules ask
        chat to stay out of. */
    this.onSay = null;

    const c = this.client;

    c.on(OPENED, () => {
      // Join (or re-join) as soon as the door opens.  On a reconnect the
      // stored token re-claims the same cat, so a server redeploy costs the
      // player a hiccup, not their whiskers.
      if (this._joinArgs) this._sendJoin();
      this._status();
    });

    c.on(CLOSED, (msg) => {
      this._slot = -1;
      this.roster.claimOwn(-1);
      // The roster is deliberately NOT cleared: the street full of cats
      // freezing in place during a blip reads better than everyone vanishing
      // and re-spawning two seconds later.  The next welcome reconciles.
      //
      // A 4010 whose deny message never arrived (a proxy ate it, a race
      // with the close frame) still gets the §4.3 reaction: the close code
      // alone carries no window, so the sentence just names no numbers.
      if (msg && msg.code === CLOSE.PROTO && this._denied !== DENY.VERSION &&
          this._denied !== VERSION_STEAM) {
        this._versionTrouble(null, null);
      }
      this._status();
    });

    c.on("welcome", (msg) => {
      // The server said yes -- now check the dialects actually overlap.  A
      // window-aware server advertises `win` {min,max} and this build only
      // has to fall INSIDE it: the wire is append-only, so a tab one
      // generation behind reads the new traffic minus the additions
      // (constants.js's §4.3 block).  A server that advertises no window
      // predates negotiation, and for that one the old strict rule is the
      // only safe reading.  Out either way: stand down rather than keep
      // speaking a dialect the far end never promised to hold, and let
      // _versionTrouble say something per-platform.
      const win = msg.win;
      const windowed = !!win && typeof win === "object" &&
        Number.isInteger(win.min) && Number.isInteger(win.max);
      const spoken = windowed
        ? PROTO_VERSION >= win.min && PROTO_VERSION <= win.max
        : msg.proto === PROTO_VERSION;
      if (!spoken) {
        this._versionTrouble(windowed ? win.min : null, windowed ? win.max : null);
        c.close();
        this._status();
        return;
      }
      // In the window: this deploy's skew story is settled, so the next
      // mismatch (a future redeploy mid-session) gets its own one reload.
      forgetReloaded();
      if (Number.isInteger(msg.slot)) {
        this._slot = msg.slot;
        this.roster.claimOwn(msg.slot);
      }
      if (typeof msg.id === "string") this._id = msg.id;
      this._denied = null;

      // The roster in one message: reconcile, do not just add.  After a
      // reconnect our set may hold cats that left while we were gone, and a
      // stale entry would stand frozen in the garden forever.
      const listed = new Set([this._slot]);
      if (Array.isArray(msg.cats)) {
        for (const entry of msg.cats) {
          if (!entry || typeof entry !== "object" || !Number.isInteger(entry.slot)) continue;
          listed.add(entry.slot);
          this.roster.setRecord(entry.slot, entry.rec);
        }
      }
      for (let i = 0; i < this.roster.slots.length; i++) {
        if (this.roster.slots[i] && !listed.has(i)) this.roster.remove(i);
      }
      this._emitRoster();
      this._status();
    });

    c.on("claim", (msg) => {
      if (Number.isInteger(msg.slot)) {
        this._slot = msg.slot;
        this.roster.claimOwn(msg.slot);
      }
      if (typeof msg.id === "string") this._id = msg.id;
      if (typeof msg.token === "string" && msg.token) {
        // Saved here, unconditionally: the token arrives exactly once, on
        // exactly this socket, and a callback that forgot to save it would
        // orphan the cat on the first reload.
        saveToken(msg.token);
        if (this._joinArgs) this._joinArgs.token = msg.token;
      }
      this.callbacks.onClaim?.({ slot: msg.slot, id: msg.id, token: msg.token });
      this._status();
    });

    c.on("deny", (msg) => {
      this._denied = typeof msg.why === "string" ? msg.why : "denied";
      this._deniedOpen = msg.open === true;
      if (msg.why === DENY.VERSION) {
        // The server refused the dialect outright, with its window in the
        // payload; the 4010 close is already on its way behind this
        // message.  Not a condition to retry around (client.js's outdated
        // flag stops the reconnect loop) -- only to react to, per platform.
        this._versionTrouble(
          Number.isInteger(msg.min) ? msg.min : null,
          Number.isInteger(msg.max) ? msg.max : null,
        );
        this._status();
        return;
      }
      if (msg.why === DENY.STALE && this._joinArgs && !this._retriedStale) {
        // The token names a cat the server no longer has.  That is not a
        // condition the player can fix, but we can: forget it and ask for a
        // fresh cat, once.  (Once -- a server that denies the fresh join too
        // is telling us something a loop would shout over.)
        this._retriedStale = true;
        saveToken(null);
        this._joinArgs.token = null;
        this._sendJoin();
      }
      // FULL and BUSY are surfaced and left alone: "the street is full" and
      // "your cat is being played in another tab" are the player's news,
      // not conditions to retry our way around.
      this._status();
    });

    c.on("cat", (msg) => {
      this.roster.setRecord(msg.slot, msg.rec);
      this._emitRoster();
    });

    c.on("gone", (msg) => {
      if (Number.isInteger(msg.slot)) this.roster.remove(msg.slot);
      this._emitRoster();
    });

    c.on("needs", (msg) => {
      this.callbacks.onNeeds?.(Array.isArray(msg.w) ? msg.w : [], msg.p);
    });

    c.on("bliss", (msg) => {
      this.callbacks.onBliss?.(msg.slot, msg.place);
    });

    c.on("say", (msg) => {
      // Our own slot is dropped on purpose: the local bubble was drawn at
      // pick time (zero latency, and it is all there is offline), so the
      // echo the broadcast carries would double it.  The index is checked
      // for shape only -- whether it names a phrase THIS build knows is
      // phraseText's question, answered where the bubble is drawn.
      if (!Number.isInteger(msg.slot) || msg.slot === this._slot) return;
      if (!Number.isInteger(msg.p)) return;
      this.onSay?.(msg.slot, msg.p);
    });

    c.on("pong", () => this._status());   // the rtt figure just refreshed
  }

  connect() {
    this.client.connect();
    // If connect decided there is no network to have (file://, no host),
    // say so once so the HUD can not-mention it.
    if (this.client.offline) this._status();
  }

  /**
   * Introduce our cat.  Safe to call before the socket opens -- the args are
   * held and sent on OPENED, and re-sent on every reconnect.
   */
  join({ name = "", look = null, token = null } = {}) {
    this._joinArgs = { name, look, token: token ?? localToken() };
    if (this.client.connected) this._sendJoin();
  }

  _sendJoin() {
    const j = this._joinArgs;
    // `v` is what makes the version window negotiable at all: the server
    // judges it against [PROTO_MIN, PROTO_VERSION] before the token is even
    // read (world.claim).  Clients that predate this field never send it
    // and are read as v1 there -- which is why adding it strands nobody.
    this.client.send({ t: "join", name: j.name, look: j.look, token: j.token,
                       v: PROTO_VERSION });
  }

  /** Rate-limited and change-suppressed in client.js; called every frame. */
  sendPose(player, state) {
    if (this._slot < 0) return;   // not joined: the server has no row for us
    this.client.sendPose(player, state);
  }

  /** The customizer's onLook lands here, per input event; see LOOK_MS. */
  sendLook(look) {
    if (this._joinArgs) this._joinArgs.look = look;   // reconnects wear the latest
    if (!this.client.connected || this._slot < 0) return;
    this._lookPending = look;
    if (this._lookTimer) return;
    const since = performance.now() - this._lookSentAt;
    const wait = Math.max(0, LOOK_MS - since);
    this._lookTimer = setTimeout(() => {
      this._lookTimer = 0;
      this._lookSentAt = performance.now();
      if (this._lookPending && this.client.connected) {
        this.client.send({ t: "look", look: this._lookPending });
        this._lookPending = null;
      }
    }, wait);
  }

  sendName(name) {
    if (this._joinArgs) this._joinArgs.name = name;
    if (!this.client.connected || this._slot < 0) return;
    this._namePending = name;
    if (this._nameTimer) return;
    this._nameTimer = setTimeout(() => {
      this._nameTimer = 0;
      if (this._namePending !== null && this.client.connected) {
        this.client.send({ t: "name", name: this._namePending });
        this._namePending = null;
      }
    }, NAME_MS);
  }

  /** One quick-chat phrase, by index into shared/phrases.js.  No throttle
      here, unlike look/name: chat.js runs the client mirror of the server's
      say bucket BEFORE calling this, so a phrase that reaches this method
      is one the server will accept -- pacing it again would only let the
      two ends disagree about which phrase got through. */
  say(p) {
    if (!this.client.connected || this._slot < 0) return;
    this.client.send({ t: "say", p });
  }

  meow() {
    if (!this.client.connected || this._slot < 0) return;
    this.client.send({ t: "meow" });
  }

  /** Once per frame, before the render: advance the render-past clock, then
      pose every remote cat against it. */
  update(dt) {
    this.client.update(dt);
    this.roster.update(dt, this.client);
    // Human/brain handovers happen in the snapshot stream, not in roster
    // messages, so the listing is re-announced when the roster noticed one.
    if (this.roster.humanRev !== this._humanRev) {
      this._humanRev = this.roster.humanRev;
      this._emitRoster();
    }
  }

  get slot() { return this._slot; }
  get connected() { return this.client.connected; }
  get offline() { return this.client.offline; }

  /**
   * §4.3's per-platform reaction to version skew, and the ONLY place in the
   * client that branches on platform -- the wire handling above it is one
   * dialect everywhere.  (This lives here and not in app/boot.js only
   * because boot's onStatus wiring belongs to another workstream this wave;
   * the branch is UI policy and could move up whenever boot wants it.)
   *
   * Desktop: no reload -- a reload cannot update a Steam binary.  The shell
   * is told (so "Visit" can grey out and the player stays in the LOCAL
   * world, which always matches because the binary ships its own server --
   * the §4.3 never-bricked property), and the join card shows the Steam
   * sentence via the VERSION_STEAM display key.
   *
   * Web: the page came FROM this server, so one reload fetches the matching
   * client -- automatically, once per tab (the flag lives in
   * sessionStorage: it must survive the reload it causes and die with the
   * tab).  If the reloaded page STILL mismatches -- a cache, a mid-deploy
   * race -- the card shows the refresh sentence instead of reloading
   * forever.  Where the flag cannot be trusted to stick (storage throwing),
   * the reload is skipped for the same reason: a loop nobody can interrupt
   * is worse than a sentence.
   *
   * @param {number | null} min  the server's window floor, if it said
   * @param {number | null} max  the server's window ceiling, if it said
   */
  _versionTrouble(min, max) {
    const bridge = desktopBridge();
    if (bridge) {
      this._denied = VERSION_STEAM;
      try {
        bridge.visitDenied?.({ mine: PROTO_VERSION, min, max });
      } catch {
        // A half-loaded preload must not take the renderer down with it.
      }
      return;
    }
    if (!hasReloaded() && markReloaded()) {
      try {
        location.reload();
        return;
      } catch {
        // No location to reload (a headless import): fall through to the
        // sentence, which is the honest answer there anyway.
      }
    }
    this._denied = DENY.VERSION;
  }

  _status() {
    this.callbacks.onStatus?.({
      connected: this.client.connected,
      rtt: this.client.rtt,
      denied: this._denied,
      deniedOpen: this._deniedOpen === true,
      offline: this.client.offline,
      replaced: this.client.replaced,
      outdated: this.client.outdated,
    });
  }

  _emitRoster() {
    if (!this.callbacks.onRoster) return;
    const out = [];
    for (let i = 0; i < this.roster.slots.length; i++) {
      const e = this.roster.slots[i];
      // `human` is null until a snapshot has said either way -- counting a
      // cat as AI for the half-second before its first row would flicker
      // the HUD's head-count on every join.
      if (e) out.push({ slot: i, name: e.name, human: e.human ?? undefined });
      else if (i === this._slot) {
        // Our own slot: by definition somebody is at the wheel.
        out.push({ slot: i, name: this._joinArgs?.name || "", mine: true, human: true });
      }
    }
    this.callbacks.onRoster?.(out);
  }
}

// ---------------------------------------------------------------------------
// The claim token lives in identity.js now (ARCH-PLAN §4.3, WS-15): one
// provider for both shelves -- localStorage on the web, byte-for-byte the
// block that sat here, and the desktop bridge's size-capped file store when
// the shell is present (the WS-17 contract and the hand-recovery doctrine
// are documented there).  Re-exported so app/boot.js's import path and the
// claim path above keep their spelling.
// ---------------------------------------------------------------------------
export { localToken, saveToken };

// ---------------------------------------------------------------------------
// The reload-once flag, in sessionStorage -- _versionTrouble's web branch.
// Session, not local, and that is the whole design: the flag must SURVIVE
// the reload it causes (so the reloaded page cannot reload again) and die
// with the tab (so tomorrow's visit gets tomorrow's one reload).  Same
// defensive posture as the token block above, with one twist: markReloaded
// reports whether the write verifiably stuck, because the caller is about
// to location.reload() on the strength of it -- an optimistic true from a
// throwing storage would reload in a loop nobody can interrupt.
// ---------------------------------------------------------------------------
// No legacy-key adoption for this one, unlike the localStorage shelves:
// it lives in sessionStorage and dies with the tab, so the oldest value it
// could possibly hold is from this same session and this same build.
const RELOADED_KEY = "catworld.protoReloaded";

/** sessionStorage, or null where even touching it throws. */
function session() {
  try {
    return globalThis.sessionStorage || null;
  } catch {
    return null;
  }
}

/** Has this tab already spent its one automatic reload? */
function hasReloaded() {
  const s = session();
  if (!s) return false;
  try {
    return s.getItem(RELOADED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Spend the reload; true only when the flag verifiably stuck. */
function markReloaded() {
  const s = session();
  if (!s) return false;
  try {
    s.setItem(RELOADED_KEY, "1");
    return s.getItem(RELOADED_KEY) === "1";
  } catch {
    return false;
  }
}

/** An in-window welcome landed: re-arm the next deploy's one reload. */
function forgetReloaded() {
  const s = session();
  if (!s) return;
  try {
    s.removeItem(RELOADED_KEY);
  } catch {}
}
